import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import {
  AsyncSection,
  DataTable,
  Form,
  FormError,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  type Column,
} from '../../components';
import {
  StageStatus,
  WalkMode,
  WalkStage,
  type CompanyResponse,
  type EsocialPortalSessionCompany,
  type EsocialPortalSessionResponse,
} from '../../api';
import { formatDateTime, formatInteger } from '../../format';
import { staffEsocialPortalWalkPath } from '../../routes/paths';
import {
  useCompanies,
  useEsocialPortalBrowser,
  useEsocialPortalSession,
  useRunEsocialPortalSession,
} from '../queries';
import {
  COMPANY_OUTCOME_LABELS,
  COMPANY_OUTCOME_TONES,
  PORTAL_SESSION_COPY,
  PORTAL_WALK_COPY,
  SESSION_OUTCOME_LABELS,
  SESSION_OUTCOME_TONES,
  SESSION_SKIPPED_REASONS,
  STAGE_FAILURE_MESSAGES,
  STAGE_STATUS_LABELS,
  STAGE_STATUS_TONES,
  WALK_MODE_LABELS,
  WALK_STAGES_OF,
  WALK_STAGE_LABELS,
  sessionStartFailureMessage,
} from './portalWalk';

/** The most companies one session walks (#479), as the API caps it. */
const MAX_COMPANIES = 50;

/** How many search matches the picker shows at once. */
const MATCHES_SHOWN = 8;

/**
 * The walks tab's *Sessão* panel (#479): one gov.br login, then every company
 * picked, in the order picked.
 *
 * Like a single walk, the run endpoint answers when the session is over, so
 * the session in progress is the mutation's pending state -- with #477's
 * waiting-for-you copy when the browser is a remote one with a person at it.
 * The finished session lives in the address (`?sessao=`), so it survives a
 * reload and can be pasted into a conversation; each company's row links to
 * that company's walk on the same tab.
 *
 * Mounted only for a principal holding `esocial:write`, the permission the
 * session is gated with.
 */
export function EsocialPortalSessionPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('sessao');

  const showSession = (answer: EsocialPortalSessionResponse) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set('sessao', answer.id);
      return params;
    });
  };

  return (
    <>
      <StartSession onFinished={showSession} />
      {sessionId !== null && <SessionResult sessionId={sessionId} />}
    </>
  );
}

function StartSession({
  onFinished,
}: {
  onFinished: (answer: EsocialPortalSessionResponse) => void;
}) {
  const companies = useCompanies();
  const run = useRunEsocialPortalSession();
  const remote = useEsocialPortalBrowser().data?.remote === true;
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<readonly CompanyResponse[]>([]);
  const [mode, setMode] = useState<WalkMode>(WalkMode.REQUEST);
  const [stopAfter, setStopAfter] = useState<WalkStage | ''>('');
  const [chosenError, setChosenError] = useState<string | undefined>(undefined);

  const specific = sessionStartFailureMessage(run.error);
  const picked = new Set(chosen.map((company) => company.id));
  const needle = search.trim().toLocaleLowerCase('pt-BR');
  const matches =
    needle === ''
      ? []
      : (companies.data?.items ?? [])
          .filter(
            (company) =>
              !picked.has(company.id) && company.name.toLocaleLowerCase('pt-BR').includes(needle),
          )
          .slice(0, MATCHES_SHOWN);
  // `login` stops after the shared login; any other stage of the mode applies
  // to every company. A stage the new mode lacks would be refused, so it resets.
  const stages = WALK_STAGES_OF[mode];

  const chooseMode = (next: WalkMode | '') => {
    if (next === '') {
      return;
    }
    setMode(next);
    if (stopAfter !== '' && !WALK_STAGES_OF[next].includes(stopAfter)) {
      setStopAfter('');
    }
  };

  const add = (company: CompanyResponse) => {
    setChosenError(undefined);
    setChosen((previous) => (previous.length >= MAX_COMPANIES ? previous : [...previous, company]));
    setSearch('');
  };

  const remove = (companyId: string) => {
    setChosen((previous) => previous.filter((company) => company.id !== companyId));
  };

  const move = (index: number, by: -1 | 1) => {
    setChosen((previous) => {
      const target = index + by;
      if (target < 0 || target >= previous.length) {
        return previous;
      }
      const next = [...previous];
      [next[index], next[target]] = [
        next[target] as CompanyResponse,
        next[index] as CompanyResponse,
      ];
      return next;
    });
  };

  const submit = () => {
    if (chosen.length === 0) {
      setChosenError(PORTAL_SESSION_COPY.chosenMissing);
      return;
    }
    run.mutate(
      {
        company_ids: chosen.map((company) => company.id),
        mode,
        ...(stopAfter !== '' && { stop_after: stopAfter }),
      },
      { onSuccess: onFinished },
    );
  };

  return (
    <Panel title={PORTAL_SESSION_COPY.title} description={PORTAL_SESSION_COPY.description}>
      <Form onSubmit={submit} aria-label={PORTAL_SESSION_COPY.title}>
        <FormError error={run.error} {...(specific !== undefined && { message: specific })} />
        <TextField
          label={PORTAL_SESSION_COPY.search}
          value={search}
          onChange={setSearch}
          hint={
            companies.isPending ? PORTAL_WALK_COPY.companiesLoading : PORTAL_SESSION_COPY.searchHint
          }
          disabled={run.isPending}
        />
        {needle !== '' && (
          <ul className="list" aria-label={PORTAL_SESSION_COPY.search}>
            {matches.length === 0 && (
              <li className="table__muted">{PORTAL_SESSION_COPY.searchEmpty}</li>
            )}
            {matches.map((company) => (
              <li key={company.id}>
                {company.name}
                {company.cnpj === null && (
                  <span className="table__muted"> ({PORTAL_SESSION_COPY.noCnpj})</span>
                )}{' '}
                <button
                  type="button"
                  className="button button--ghost"
                  aria-label={PORTAL_SESSION_COPY.add(company.name)}
                  onClick={() => add(company)}
                  disabled={run.isPending || chosen.length >= MAX_COMPANIES}
                >
                  {PORTAL_SESSION_COPY.addShort}
                </button>
              </li>
            ))}
          </ul>
        )}
        <h3>{PORTAL_SESSION_COPY.chosen}</h3>
        {chosen.length === 0 ? (
          <p className="table__muted">{PORTAL_SESSION_COPY.chosenEmpty}</p>
        ) : (
          <ol aria-label={PORTAL_SESSION_COPY.chosen}>
            {chosen.map((company, index) => (
              <li key={company.id}>
                {company.name}{' '}
                <button
                  type="button"
                  className="button button--ghost"
                  aria-label={PORTAL_SESSION_COPY.up(company.name)}
                  onClick={() => move(index, -1)}
                  disabled={run.isPending || index === 0}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  aria-label={PORTAL_SESSION_COPY.down(company.name)}
                  onClick={() => move(index, 1)}
                  disabled={run.isPending || index === chosen.length - 1}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  aria-label={PORTAL_SESSION_COPY.remove(company.name)}
                  onClick={() => remove(company.id)}
                  disabled={run.isPending}
                >
                  {PORTAL_SESSION_COPY.removeShort}
                </button>
              </li>
            ))}
          </ol>
        )}
        {chosenError !== undefined && (
          <p className="field__error" role="alert">
            {chosenError}
          </p>
        )}
        <SelectField
          label={PORTAL_WALK_COPY.mode}
          value={mode}
          onChange={chooseMode}
          options={Object.values(WalkMode).map((value) => ({
            value,
            label: WALK_MODE_LABELS[value],
          }))}
          hint={PORTAL_WALK_COPY.modeHint}
          required
          disabled={run.isPending}
        />
        <SelectField
          label={PORTAL_WALK_COPY.stopAfter}
          value={stopAfter}
          onChange={setStopAfter}
          options={stages.map((stage) => ({
            value: stage,
            label:
              stage === WalkStage.LOGIN
                ? PORTAL_SESSION_COPY.stopAfterLogin
                : WALK_STAGE_LABELS[stage],
          }))}
          placeholder={PORTAL_WALK_COPY.stopAfterAll}
          hint={PORTAL_SESSION_COPY.stopAfterHint}
          disabled={run.isPending}
        />
        <SubmitButton
          pending={run.isPending}
          pendingLabel={remote ? PORTAL_SESSION_COPY.startingRemote : PORTAL_SESSION_COPY.starting}
        >
          {PORTAL_SESSION_COPY.start}
        </SubmitButton>
        {run.isPending && (
          <p className="panel__footnote" role="status">
            {remote ? PORTAL_SESSION_COPY.inProgressRemote : PORTAL_SESSION_COPY.inProgress}
          </p>
        )}
      </Form>
    </Panel>
  );
}

function SessionResult({ sessionId }: { sessionId: string }) {
  const session = useEsocialPortalSession(sessionId);
  const companies = useCompanies();
  const names = new Map((companies.data?.items ?? []).map((company) => [company.id, company.name]));
  return (
    <AsyncSection
      state={session}
      loadingLabel={PORTAL_SESSION_COPY.resultTitle}
      errorTitle={PORTAL_SESSION_COPY.resultFailed}
    >
      {(data) => <SessionResultBody session={data} names={names} />}
    </AsyncSection>
  );
}

function companyDetail(entry: EsocialPortalSessionCompany): string {
  if (entry.skipped_reason) {
    return SESSION_SKIPPED_REASONS[entry.skipped_reason];
  }
  if (entry.failure_code) {
    return STAGE_FAILURE_MESSAGES[entry.failure_code];
  }
  if (entry.failed_stage) {
    return `${WALK_STAGE_LABELS[entry.failed_stage]}: ${entry.failure ?? ''}`.trim();
  }
  return '—';
}

function SessionResultBody({
  session,
  names,
}: {
  session: EsocialPortalSessionResponse;
  names: ReadonlyMap<string, string>;
}) {
  const login = session.login;
  const columns: readonly Column<EsocialPortalSessionCompany>[] = [
    {
      key: 'position',
      header: PORTAL_SESSION_COPY.position,
      render: (entry) => formatInteger(entry.position + 1),
    },
    {
      key: 'company',
      header: PORTAL_SESSION_COPY.company,
      render: (entry) => (
        <>
          {names.get(entry.company_id) ?? entry.employer_cnpj_formatted}
          <span className="table__muted"> {entry.employer_cnpj_formatted}</span>
        </>
      ),
    },
    {
      key: 'outcome',
      header: PORTAL_SESSION_COPY.outcome,
      render: (entry) => (
        <span className={`badge badge--${COMPANY_OUTCOME_TONES[entry.outcome]}`}>
          {COMPANY_OUTCOME_LABELS[entry.outcome]}
        </span>
      ),
    },
    {
      key: 'detail',
      header: PORTAL_SESSION_COPY.detail,
      render: (entry) => (
        <>
          {companyDetail(entry)}
          {entry.portal_message && <span className="table__muted"> “{entry.portal_message}”</span>}
          {entry.remedy && <span className="table__muted"> {entry.remedy}</span>}
        </>
      ),
    },
    {
      key: 'duration',
      header: PORTAL_SESSION_COPY.companyDuration,
      render: (entry) =>
        entry.duration_seconds === null || entry.duration_seconds === undefined
          ? '—'
          : PORTAL_SESSION_COPY.durationValue(entry.duration_seconds),
    },
    {
      key: 'walk',
      header: PORTAL_SESSION_COPY.walk,
      render: (entry) =>
        entry.walk_id ? (
          <Link to={staffEsocialPortalWalkPath(entry.company_id, entry.walk_id)}>
            {PORTAL_SESSION_COPY.openWalk}
          </Link>
        ) : (
          <span className="table__muted">{PORTAL_SESSION_COPY.noWalk}</span>
        ),
    },
  ];

  return (
    <Panel title={PORTAL_SESSION_COPY.resultTitle}>
      <dl className="definitions">
        <dt>{PORTAL_WALK_COPY.outcome}</dt>
        <dd>
          {session.outcome ? (
            <span className={`badge badge--${SESSION_OUTCOME_TONES[session.outcome]}`}>
              {SESSION_OUTCOME_LABELS[session.outcome]}
            </span>
          ) : (
            '—'
          )}
        </dd>
        <dt>{PORTAL_SESSION_COPY.login}</dt>
        <dd>
          <span className={`badge badge--${STAGE_STATUS_TONES[login.status]}`}>
            {STAGE_STATUS_LABELS[login.status]}
          </span>
          {login.status === StageStatus.FAILED && login.failure_code && (
            <span> {STAGE_FAILURE_MESSAGES[login.failure_code]}</span>
          )}
          {login.status === StageStatus.FAILED && login.failure && (
            <span className="table__muted"> {login.failure}</span>
          )}
          {login.captcha_solved_by_person === true && (
            <span className="table__muted">
              {' '}
              {PORTAL_WALK_COPY.captchaSolvedValue(login.captcha_solve_seconds)}
            </span>
          )}
          {login.two_step_completed_by_person === true && (
            <span className="table__muted">
              {' '}
              {PORTAL_WALK_COPY.twoStepCompleted}:{' '}
              {PORTAL_WALK_COPY.twoStepCompletedValue(login.two_step_seconds)}
            </span>
          )}
        </dd>
        <dt>{PORTAL_WALK_COPY.startedAt}</dt>
        <dd>{formatDateTime(session.started_at)}</dd>
        <dt>{PORTAL_SESSION_COPY.duration}</dt>
        <dd>
          {session.duration_seconds === null || session.duration_seconds === undefined
            ? '—'
            : PORTAL_SESSION_COPY.durationValue(session.duration_seconds)}
        </dd>
        <dt>{PORTAL_WALK_COPY.requestedBy}</dt>
        <dd>{session.requested_by_display_name}</dd>
      </dl>
      <DataTable
        id="esocial-portal-session-companies"
        caption={PORTAL_SESSION_COPY.resultCaption}
        columns={columns}
        rows={session.companies}
        rowKey={(entry) => entry.company_id}
      />
    </Panel>
  );
}
